import { Injectable, inject } from '@angular/core';
import { of, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  OmniSearchResult,
} from '@brighthub/search';
import { BrightAiOmniSearchProvider } from '@brighthub/brightai-search';
import {
  ClientSessionService,
  UiSession,
  ClientSubjectData
} from '@brighthub/uisession';
import { ClientSearchService } from '../client-search.service';
import { GenericResponseCode } from '@brighthub/api';
import { AmplitudeService } from '@brighthub/logging';
import { ClientSearchResultComponent } from '../client-search-result/client-search-result.component';

// we convert the ClientSubjectData interface to a UiSession interface so we
// can reuse client info for the search results
const mockUiSessionFromSubject = (subject: ClientSubjectData): UiSession => ({
  uiSessionId: 'temp-' + subject.guid,
  oktaUserId: '',
  subjectId: subject.guid,
  subjectType: 'client',
  conversationId: undefined,
  createdDate: '',
  subjectData: subject,
  state: 'active',
});

@Injectable({
  providedIn: 'root',
})
export class ClientSearchProvider extends BrightAiOmniSearchProvider<UiSession> {
  id = 'client';
  label = 'Search for Client';
  override description = 'Search for a client by GUID.';
  override shortcut = '@client';
  rolesAllowed = [];

  private _clientService = inject(ClientSessionService);
  private _clientSearchService = inject(ClientSearchService);
  private _amplitude = inject(AmplitudeService);

  search(query: string): Observable<OmniSearchResult<UiSession>[]> {
    const q = query.trim().toLowerCase();

    if (!q) return of([]);

    // 1️⃣ Local match (using ClientSessionService)
    const localMatches = this._clientService
      .sessions()
      .filter(
        (s) =>
          s.subjectData.guid.toLowerCase().includes(q) ||
          `${s.subjectData.firstName} ${s.subjectData.lastName}`
            .toLowerCase()
            .includes(q)
      );

    if (localMatches.length > 0) {
      this._amplitude.logEvent('client_search_local_match', {
        query: q,
        count: localMatches.length,
      });
      return of(
        localMatches.map((s) => ({
          title: `${s.subjectData.firstName} ${s.subjectData.lastName}`,
          description: s.subjectData.guid,
          data: s,
        }))
      );
    }

    // 2️⃣ Remote match (via ClientSearchService)
    if (q.length < 9) return of([]); // Avoid hammering API with short GUIDs

    return this._clientSearchService.searchByGuid(q).pipe(
      map((res) => {
        if (
          res.result.code === GenericResponseCode.SERVICE_OK &&
          res.payload?.guid
        ) {
          const mockSession = mockUiSessionFromSubject(res.payload);
          this._amplitude.logEvent('client_search_remote_success', {
            query: q,
          });
          return [
            {
              title: `${res.payload.firstName} ${res.payload.lastName}`,
              description: res.payload.guid,
              data: mockSession,
            },
          ];
        } else {
          this._amplitude.logEvent('client_search_remote_empty', { query: q });
          return [];
        }
      }),
      catchError((err) => {
        if (err.status !== 404) {
          this._amplitude.logEvent('client_search_remote_error', {
            query: q,
            message: err.message,
            status: err.status,
          });
        }
        return of([]);
      })
    );
  }

  loadComponent = async () => ClientSearchResultComponent;
}
